import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TypewriterOptions } from 'app/modules/ai/types';

const DEFAULT_INTERVAL_MS = 30;
const DEFAULT_WORDS_PER_TICK = 2;

interface UseTypewriterEffectReturn {
  displayedText: string;
  isComplete: boolean;
  reset: () => void;
}

export const useTypewriterEffect = (
  text: string,
  enabled: boolean,
  options?: TypewriterOptions,
): UseTypewriterEffectReturn => {
  const {
    intervalMs = DEFAULT_INTERVAL_MS,
    wordsPerTick = DEFAULT_WORDS_PER_TICK,
  } = options ?? {};

  const { clampedIntervalMs, clampedWordsPerTick } = useMemo(
    () => ({
      clampedIntervalMs: Math.max(1, intervalMs),
      clampedWordsPerTick: Math.max(1, wordsPerTick),
    }),
    [intervalMs, wordsPerTick],
  );

  // Compute words synchronously so they are available on the first render
  const words = useMemo(() => (text ? text.split(/(\s+)/) : []), [text]);

  const [wordIndex, setWordIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setWordIndex(0);
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Reset word index when the source text changes while animating
  useEffect(() => {
    if (enabled) {
      setWordIndex(0);
    }
  }, [text, enabled]);

  // Run the animation loop
  useEffect(() => {
    if (!enabled || !text || wordIndex >= words.length) {
      return undefined;
    }

    timerRef.current = setTimeout(() => {
      setWordIndex((prev) =>
        Math.min(prev + clampedWordsPerTick, words.length),
      );
    }, clampedIntervalMs);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    enabled,
    text,
    wordIndex,
    clampedIntervalMs,
    clampedWordsPerTick,
    words.length,
  ]);

  if (!enabled) {
    return { displayedText: text, isComplete: true, reset };
  }

  const displayedText = words.slice(0, wordIndex).join('');
  const isComplete = wordIndex >= words.length;

  return { displayedText, isComplete, reset };
};
